import { Avatar, Button, Card, Col, Empty, Flex, Pagination, Result, Row, Select, Skeleton, Typography } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useAddressHelper } from "utils/useAddressHelper";
import { useGetJobs } from "api/jobs/useGetJobs";
import { JobInterface, JobLocation } from "api/jobs/dto";
import SaveIcon from "../../assets/save.svg";
import CompanyIcon from "../../assets/rectangle.svg";
import { calculateDaysAgo, formatDate } from "utils/date";
import { JobTypeValues } from "enum/job-type";
import { useNavigate } from "react-router-dom";
import { storage } from "utils/storage";
import { useState } from "react";
import { QueryParamsStatus, useFilterPagination } from "hooks/useFilterPagination";
import { useIsMobile } from "hooks/useIsMobile";
import enumeratorImg from "../../assets/EnumeratorRegister.png";
import {
  ArrowRightOutlined,
  ArrowLeftOutlined,
  FileSearchOutlined,
  BellOutlined,
  UserOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";

const { Option } = Select;
const { Text } = Typography;

const Hero = () => {
  const { getProvinceOptions, getDistrictOptions, address, setAddress } =
    useAddressHelper();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const user = storage.getUserFromStorage();
  const { queryParamaters, onFilterChange, onTableChange } =
    useFilterPagination();
  const { data, isError, isPending, isSuccess } = useGetJobs(queryParamaters);
  const [showFirstDiv, setShowFirstDiv] = useState(true);

  const handleFilter = () => {
    document
      .querySelector("#data-wrapper")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  const toggleDivs = () => {
    setShowFirstDiv(!showFirstDiv);
  };

  const getFormattedLocations = (locations?: JobLocation[]): string => {
    if (!locations || locations.length === 0) return "Location not available";
    const firstFormattedLocation = `${locations[0].district}`;
    const additionalCount = locations.length > 1 ? `, ... and ${locations.length - 1} more` : "";
    return `${firstFormattedLocation} ${additionalCount}`;
  };

  const renderJobsContent = () => {
    if (isError) {
      return (
        <Result
          status="error"
          title="Server Error"
          subTitle="There seems to be problem with the server. Please check after few minutes."
        ></Result>
      );
    }

    if (isPending) {
      return (
        <Card style={{ width: "100%", marginTop: 16, height: "700px" }}>
          <Skeleton active />
          <Skeleton active />
          <Skeleton active />
          <Skeleton active />
          <Skeleton active />
        </Card>
      );
    }

    if (data?.data?.length === 0) {
      return (
        <Card
          style={{
            height: "180px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Empty />
        </Card>
      );
    }

    if (isSuccess && data?.data?.length > 0) {
      return (
        <>
          <Row gutter={[32, 32]}>
            {data?.data?.map((i: JobInterface) => (
              <Col key={i.id} xs={24} sm={24} md={24} lg={12} xl={12}>
                <Card hoverable>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "start",
                    }}
                  >
                    <h3 className="text-2xl md:text-2xl font-medium  text-black  mb-2">
                      {i?.jobTitle}
                    </h3>
                    <img alt="#" src={SaveIcon} />
                  </div>

                  <Flex gap={"middle"} style={{ margin: "14px 0px" }}>
                    <Avatar src={CompanyIcon} size={60} />
                    <Flex vertical flex={1}>
                      <Flex justify="space-between">
                        <h3 className="text-lg md:text-lg font-semibold  text-black">
                          {i?.companyName}
                        </h3>
                        <div>{calculateDaysAgo(i?.createdAt.toDate())} days ago</div>
                      </Flex>
                      <Text className="text-sm text-gray-500">
                        {getFormattedLocations(i?.joblocation)}

                      </Text>
                    </Flex>
                  </Flex>
                  <div className="flex flex-col md:flex-row md:justify-between gap-y-5 py-2 px-2 ml-2">
                    <Text className="font-medium text-gray-700">{i?.jobType}</Text>
                    <Text className="text-sm text-gray-500">Deadline: {formatDate(i?.deadlineAt.toDate())}</Text>
                  </div>

                  <div className="flex flex-col md:flex-row md:justify-between gap-y-4">
                    {i?.salary?.isPubliclyAvailable ? (
                      <div className="btn-sm text-neutral-500 bg-slate-50 px-2 ml-2 py-2 rounded-full">
                        Rs {i?.salary?.amount} {i?.salary?.period}
                      </div>
                    ) : (
                      <p></p>
                    )}
                    <button
                      onClick={() => {
                        if (!user) {
                          navigate(`/login`);
                        } else {
                          navigate(`/enumerator/job/details/${i.id}`);
                          window.scrollTo(0, 0);
                        }
                      }}
                      className="btn-sm text-white bg-primary hover:bg-primary ml-3 px-5 py-2 rounded w-28 "
                    >
                      View more
                    </button>
                  </div>

                  {/* <div className="mt-4">
                    {i?.skills?.map((s, index) => (
                      <Tag key={s + index} color={getColorOption()}>{s}</Tag>
                    ))}
                  </div> */}
                </Card>
              </Col>
            ))}
          </Row>
        </>
      );
    }
  };

  return (
    <>
      <section className="bg-[#EDF6F1]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 mb-10">
          <div className="pt-32 pb-12 md:pt-20 md:pb-10">
            <div className="text-center pb-12 md:pb-16">
              <h1 className="text-4xl md:text-6xl font-extrabold mb-4">
                Welcome to <span className="text-green-600">HireEnumerators.com!</span>
              </h1>
              <div className="max-w-5xl mx-auto">
                <p className="md:text-2xl text:lg mb-20" style={{ color: "#637381" }}>
                  You're now part of a community dedicated to impactful research and data collection.
                </p>
              </div>

              {showFirstDiv ? (
                <div className="mt-8">
                  <Row>
                    <Col xs={22} sm={22} md={8} lg={8} xl={8}>
                      <img src={enumeratorImg} alt="#" className="w-full" />
                    </Col>
                    <Col xs={22} sm={22} md={14} lg={14} xl={14}>
                      <div className="bg-white md:py-6 py-5 px-5 md:ps-10 md:pe-4 space-y-8 rounded-r-2xl md:mt-0 mt-4">
                        <p className="font-normal md:text-lg text-xs md:tracking-wide text-border text-justify md:leading-tight">
                          Your presence here speaks volumes. It tells us you’re more than an enumerator—you’re a meticulous architect of truth in a world hungry for reliable data. At HireEnumerators.com, we recognize that behind every groundbreaking study, every policy shift, and every community transformed, there’s someone like you: a professional who treats each survey as a compass, guiding decision-makers toward clarity.
                        </p>
                        <p className="font-normal md:text-lg text-xs md:tracking-wide text-border text-justify md:leading-tight">
                          This isn’t just about collecting numbers. It’s about weaving narratives from raw data—stories that expose gaps, reveal opportunities, and ignite action. You are the unsung hero ensuring research doesn’t just exist but excels, turning field notes into fuel for progress.
                        </p>
                        <p className="font-normal md:text-lg text-xs md:tracking-wide text-border text-justify md:leading-tight">
                          Together, we elevate what it means to be an enumerator, because the stakes are too high to accept anything less than rigor. Every door you knock on, every survey you complete, every detail you cross-verify doesn’t just fill a spreadsheet. It rebuilds systems. It redefines outcomes. It reminds the world that data, when gathered with purpose and care, is the ultimate equalizer.
                        </p>
                      </div>
                    </Col>
                    <Col xs={2} sm={2} md={2} lg={2} xl={2} className="flex justify-end px-1 md:items-center items-start ">

                      <ArrowRightOutlined className="md:text-6xl text-3xl text-primary font-black hover:text-green-900" onClick={toggleDivs} />

                    </Col>
                  </Row>
                </div>
              ) : (
                <div>
                  <div className="mt-8 max-w-6xl">
                    <Row className="mb-14">
                      <Col xs={2} sm={2} md={2} lg={2} xl={2} className="flex justify-start items-center">

                        <ArrowLeftOutlined className="md:text-6xl text-4xl text-primary font-black hover:text-green-900" onClick={toggleDivs} />

                      </Col>
                      <Col xs={21} sm={21} md={21} lg={21} xl={21} className="100">
                        <div className="md:mt-10 flex flex-col gap-y-10">
                          <Row gutter={[32, 16]}>
                            {[
                              {
                                icon: <UserOutlined className="text-green-500" />,
                                title: "Personalized Profile",
                                description:
                                  "Complete your profile to showcase your experience, skills, and past projects. A detailed profile helps recruiters quickly recognize your true potential.",
                              },
                              {
                                icon: <BellOutlined className="text-green-500" />,
                                title: "Browse & Apply",
                                description:
                                  "Discover the latest job listings in your region or specialty. Use our intuitive filters—by location, project duration, compensation, and more—to find opportunities that perfectly match your career goals.",
                              },
                            ].map((step, index) => (
                              <Col key={index} xs={24} sm={24} md={12} lg={12} xl={12}>
                                <Row gutter={2} align="top">
                                  <Col xs={4} sm={4} md={4} lg={4} xl={4}>
                                    <Avatar
                                      size={35}
                                      icon={step.icon}
                                      className="bg-green-100 border-none"
                                    />
                                  </Col>
                                  <Col xs={20} sm={20} md={20} lg={20} xl={20}>
                                    <h2 className="text-lg md:text-2xl font-semibold leading-8 mb-3 text-left">
                                      {step.title}
                                    </h2>
                                    <p className="font-normal md:text-base  text-border text-justify">
                                      {step.description}
                                    </p>
                                  </Col>
                                </Row>
                              </Col>
                            ))}
                          </Row>

                          <Row gutter={[32, 32]}>
                            {[
                              {
                                icon: <ClockCircleOutlined className="text-green-500" />,
                                title: "Track Applications",
                                description:
                                  "Stay informed with real-time notifications when recruiters view your profile or send you a message. Monitor your application status with ease to ensure you never miss an opportunity.",
                              },
                              {
                                icon: <FileSearchOutlined className="text-green-500" />,
                                title: "Manage Your Projects",
                                description:
                                  "Once hired, enjoy a seamless project management experience. Communicate with recruiters, track milestones, and submit reports—all from a single, user-friendly dashboard.",
                              },
                            ].map((step, index) => (
                              <Col key={index} xs={24} sm={24} md={12} lg={12} xl={12}>
                                <Row gutter={2} align="top">
                                  <Col xs={4} sm={4} md={4} lg={4} xl={4}>
                                    <Avatar
                                      size={35}
                                      icon={step.icon}
                                      className="bg-green-100 border-none"
                                    />
                                  </Col>
                                  <Col xs={20} sm={20} md={20} lg={20} xl={20}>
                                    <h2 className="text-lg md:text-2xl font-semibold leading-8 mb-3 text-left">
                                      {step.title}
                                    </h2>
                                    <p className="font-normal md:text-base text-border text-justify">
                                      {step.description}
                                    </p>
                                  </Col>
                                </Row>
                              </Col>
                            ))}
                          </Row>
                        </div>
                      </Col>
                    </Row>
                  </div>
                  <div className="bg-primary w-[98vw] -ml-[calc((100vw-100%)/2)]">
                    <div className="max-w-6xl mx-auto">
                      <p className="font-medium md:text-lg text-md py-8 text-white text-justify px-8">
                        We exist to amplify your impact, connect you with projects that match your exacting standards, and ensure your skills don’t just advance your career—they elevate an entire industry.
                      </p>
                    </div>
                  </div>
                  <div className="mt-5 ms-10">
                    <p className="md:text-lg text-md text-border text-justify">
                      Keep your profile updated and check back regularly for new opportunities. We’re here to support you every step of the way as you land your next big project!
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-24">
                <Card
                  style={{ boxShadow: "0px 5px 12px 0px #2391511F" }}
                  className="__hero__card shadow-lg max-w-5xl">
                  <Row gutter={[24, 18]} className="flex justify-around" align="middle">
                    <Col xs={24} sm={24} md={8} lg={8} xl={8} >
                      <Select
                        allowClear
                        style={{ width: "100%" }}
                        placeholder="Select Province"
                        className={isMobile ? "__job__type" : "__hero__dropdown"}
                        options={getProvinceOptions()}
                        onChange={(value) => {
                          onFilterChange(value, QueryParamsStatus.FIND_BY_PROVINCE);
                          setAddress({ ...address, province: value });
                        }}
                      />
                    </Col>

                    <Col xs={24} sm={24} md={8} lg={8} xl={8} >
                      <Select
                        allowClear
                        style={{ width: "100%" }}
                        placeholder="Select District"
                        className={isMobile ? "__job__type" : "__hero__dropdown"}
                        optionFilterProp="children"
                        filterOption={(input, option: any) => {
                          return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                        }}
                        onChange={(value) => {
                          onFilterChange(value, QueryParamsStatus.FIND_BY_DISTRICT);
                          setAddress({ ...address, district: value });
                        }}
                      >
                        {getDistrictOptions()?.map((district) => (
                          <Option key={district?.label} value={district?.label}>
                            {district?.label}
                          </Option>
                        ))}
                      </Select>
                    </Col>

                    <Col xs={24} sm={24} md={6} lg={6} xl={6} className="flex justify-center md:justify-end">
                      <Button
                        className="h-12 px-8 w-full border-2 border-none text-base flex items-center gap-2 bg-primary text-white"
                        icon={<SearchOutlined />}
                        type="primary"
                        onClick={handleFilter}
                      >
                        Search
                      </Button>
                    </Col>
                  </Row>
                </Card>

              </div>
            </div>
          </div>
        </div>
      </section>
      <div className="max-w-6xl mx-auto px-4 sm:px-6" id="data-wrapper">
        <div className="flex items-center justify-center mt-16">
          <h1 className="text-4xl md:text-6xl font-extrabold leading-tighter tracking-tighter mb-4">
            Latest Popular
            <span className="bg-clip-text text-green-600 ml-4">Job</span>
          </h1>
        </div>
        <div className="mt-8">
          <Row className="mb-8">
            <Select
              value={queryParamaters?.findByJobType}
              allowClear
              placeholder="Sort by Job Type"
              className="__job__type"
              options={[
                { value: JobTypeValues.FULL_TIME, label: "Full Time" },
                { value: JobTypeValues.PART_TIME, label: "Part Time" },
                { value: JobTypeValues.CONTRACT, label: "Contract" },
                { value: JobTypeValues.INTERNSHIP, label: "Internship" },
              ]}
              onChange={(value) =>
                onFilterChange(value, QueryParamsStatus.FIND_BY_JOB_TYPE)
              }
            />
          </Row>
          {renderJobsContent()}
        </div>

        <div className="flex items-center justify-center mt-16">
          <div className="mt-6 mb-12 pb-10">
            {data?.totalData && data.totalData > 10 && (
              <Pagination
                current={data?.page}
                total={data?.totalData}
                onChange={onTableChange}
                defaultCurrent={1}
                defaultPageSize={10}
                pageSizeOptions={undefined}
                showSizeChanger={false}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Hero;
